import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { map } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ProcessorScheduling } from 'lib/graphql';

import {
  getAttentionInfo,
  getBuyerAttentionInfo,
} from '../utils/get-attention-label';

import { JobCard } from '../producer/producer-dashboard/job-card';

export type PaletteKey =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error';

type JobsByStateProps = {
  title: string;
  paletteKey: PaletteKey;
  count: number;
  loading: boolean;
  jobs: ProcessorScheduling[];
  buyerId?: string;
  hideAttention?: boolean;
  defaultExpanded?: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
};

export function JobsByState({
  title,
  paletteKey,
  count,
  loading,
  jobs,
  buyerId,
  hideAttention,
  defaultExpanded = true,
  hasNextPage,
  hasPreviousPage,
  page,
  perPage,
  onPageChange,
}: JobsByStateProps) {
  const theme = useTheme();
  const color = theme.palette[paletteKey].main;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showPagination = hasNextPage || hasPreviousPage;

  // Mobile: accumulate jobs across pages for infinite scroll
  const [mobileJobs, setMobileJobs] = useState<ProcessorScheduling[]>([]);
  const loadedPagesRef = useRef(new Set<number>());

  useEffect(() => {
    if (!isMobile) return;
    if (loading) return;

    if (page === 1) {
      setMobileJobs(jobs);
      loadedPagesRef.current = new Set([1]);
    } else if (!loadedPagesRef.current.has(page) && jobs.length > 0) {
      loadedPagesRef.current.add(page);
      setMobileJobs((prev) => [...prev, ...jobs]);
    }
  }, [jobs, page, loading, isMobile]);

  // Reset pagination when viewport mode changes so mobile always starts from page 1
  useEffect(() => {
    if (page > 1) {
      loadedPagesRef.current = new Set<number>();
      setMobileJobs([]);
      onPageChange(1);
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayedJobs = isMobile ? mobileJobs : jobs;

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = loading && page > 1;

  const handleLoadMore = useCallback(() => {
    if (!loading && hasNextPage) {
      onPageChange(page + 1);
    }
  }, [loading, hasNextPage, page, onPageChange]);

  useEffect(() => {
    if (!isMobile || !hasNextPage || loading || !expanded) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isMobile, hasNextPage, loading, expanded, handleLoadMore]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.25 : 0.15),
      }}
    >
      {/* Row header */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.25 : 0.15),
          borderBottom: expanded ? 1 : 0,
          borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.35 : 0.2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: `${paletteKey}.main`,
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Desktop: "X to XX of total" with pagination arrows */}
          {!isMobile && showPagination && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                disabled={!hasPreviousPage || loading}
                onClick={() => onPageChange(page - 1)}
                sx={{ color: `${paletteKey}.main`, p: 0.25 }}
                aria-label="Previous page"
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  color: `${paletteKey}.main`,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {(page - 1) * perPage + 1} to {Math.min(page * perPage, count)}{' '}
                of {count}
              </Typography>
              <IconButton
                size="small"
                disabled={!hasNextPage || loading}
                onClick={() => onPageChange(page + 1)}
                sx={{ color: `${paletteKey}.main`, p: 0.25 }}
                aria-label="Next page"
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          )}
          {/* Desktop: count only when single page */}
          {!isMobile && !showPagination && (
            <Typography
              variant="caption"
              sx={{
                color: `${paletteKey}.main`,
                fontWeight: 600,
              }}
            >
              {count}
            </Typography>
          )}
          {/* Mobile: just show count */}
          {isMobile && (
            <Typography
              variant="caption"
              sx={{
                color: `${paletteKey}.main`,
                fontWeight: 600,
              }}
            >
              {count}
            </Typography>
          )}
          <IconButton
            size="small"
            sx={{ color: `${paletteKey}.main`, p: 0.25 }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>
      </Box>

      {/* Row body */}
      <Collapse in={expanded}>
        <Box
          sx={{
            p: 1.5,
            display: !loading && displayedJobs.length === 0 ? 'none' : 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 1,
          }}
        >
          {loading && page === 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4,
                width: '100%',
              }}
            >
              <CircularProgress
                size={24}
                sx={{ color: `${paletteKey}.main` }}
              />
            </Box>
          )}
          {displayedJobs.length > 0 &&
            map(displayedJobs, (job) => (
              <JobCard
                key={job._id}
                job={job}
                attentionInfo={
                  hideAttention
                    ? undefined
                    : buyerId
                      ? getBuyerAttentionInfo(job.animalHeads, buyerId)
                      : getAttentionInfo(job.animalHeads)
                }
              />
            ))}
        </Box>

        {/* Mobile: infinite scroll sentinel + loading indicator */}
        {isMobile && hasNextPage && (
          <Box
            ref={sentinelRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            {isLoadingMore && (
              <CircularProgress
                size={24}
                sx={{ color: `${paletteKey}.main` }}
              />
            )}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
